import {
  Quote,
  markIllustrationAsDownloaded,
  postIllustrationRequestRequest,
  resetIllustrationState,
  selectClient,
  selectHasActiveIllustrationRequest,
  selectIllustration,
  selectSessionAdvisor,
} from '@/model';
import {
  getQuoteId,
  isLifeInsuranceQuote,
  isTraditionalLTCQuote,
} from '@/util/quoteTypeGuards';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { CARRIER_NAMES } from '@shared/constants';
import { useLicenses } from '@/features/licenses/hooks/use-licenses';

type SupportedQuote = Quote | TraditionalLTCQuote;

interface UseIllustrationManagementProps {
  quote: SupportedQuote;
  selectedPremium?: number;
  clientPartner?: Client | null;
  fundingSourceId?: string;
}

export function useIllustrationManagement({
  quote,
  selectedPremium,
  clientPartner,
  fundingSourceId,
}: UseIllustrationManagementProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const advisor = useSelector(selectSessionAdvisor);
  const illustration = useSelector(selectIllustration);
  const hasActiveIllustrationRequest = useSelector(selectHasActiveIllustrationRequest);
  const { data: licensesData } = useLicenses();
  const [isMissingAdvisorAddressDialogOpen, setIsMissingAdvisorAddressDialogOpen] =
    useState(false);
  const quoteId = getQuoteId(quote);
  const activeRequest = illustration.activeRequests?.[quoteId];
  const completedIllustration = illustration.completedIllustrations?.[quoteId];

  useEffect(() => {
    dispatch(resetIllustrationState());
  }, [client, advisor]);

  const isTraditionalLTC = isTraditionalLTCQuote(quote);

  // Callers pass the premium to illustrate; for life quotes that's the quote's adjusted
  // premiumAmount, already clamped to its issuable [investmentMin, investmentMax] range by
  // adjustQuoteForPremium — so the illustration matches the displayed cost, no clamp here.
  const currentPremium = isTraditionalLTC
    ? quote.primaryModalPremium
    : selectedPremium || (isLifeInsuranceQuote(quote) ? quote.premiumAmount : 0);

  const isIllustrationForCurrentPremium = (illustrationPremium?: number) => {
    if (isTraditionalLTC) {
      return true;
    }
    if (!illustrationPremium) {
      return isLifeInsuranceQuote(quote) && currentPremium === quote.premiumAmount;
    }
    return illustrationPremium === currentPremium;
  };

  const hasValidCompletedIllustration =
    completedIllustration?.pdfUrl &&
    isIllustrationForCurrentPremium(completedIllustration.premiumAmount);

  const hasValidActiveIllustration =
    activeRequest?.pdfUrl && isIllustrationForCurrentPremium(activeRequest.premiumAmount);

  const hasIllustrationPdf = hasValidCompletedIllustration || hasValidActiveIllustration;

  const isActiveStatus =
    activeRequest?.status === 'initiated' || activeRequest?.status === 'polling';
  const isCreatingIllustration =
    isActiveStatus && isIllustrationForCurrentPremium(activeRequest.premiumAmount);

  const isThisQuoteBeingCreated = isCreatingIllustration;
  const isAnotherQuoteBeingCreated = hasActiveIllustrationRequest && !isThisQuoteBeingCreated;

  const handleViewPdf = (event: React.MouseEvent) => {
    event.stopPropagation();

    let pdfUrl: string | null = null;
    let requestId: string | null = null;

    if (hasValidCompletedIllustration && completedIllustration) {
      pdfUrl = completedIllustration.pdfUrl;
      requestId = completedIllustration.requestId;
    } else if (hasValidActiveIllustration && activeRequest) {
      pdfUrl = activeRequest.pdfUrl;
      requestId = activeRequest.requestId;
    }

    if (pdfUrl && requestId) {
      window.open(pdfUrl, '_blank');
      dispatch(markIllustrationAsDownloaded({ requestId }));
    }
  };

  const startQuoteGeneration = () => {
    if (!client?.intakeSurvey?.clientZipCode) {
      console.error('Client zipcode is not available');
      return;
    }

    const clientData = isTraditionalLTC
      ? {
          name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
          age: quote.primaryAge,
          gender: quote.primaryGender,
        }
      : {
          name: `${client.clientFirstName || ''} ${client.clientLastName || ''}`.trim(),
          age: client.intakeSurvey?.clientAge || 0,
          gender: client.intakeSurvey?.clientGenderAtBirth,
        };

    const partnerData =
      !isTraditionalLTC && clientPartner
        ? {
            name: `${clientPartner.clientFirstName || ''} ${clientPartner.clientLastName || ''}`.trim(),
            age: clientPartner.intakeSurvey?.clientAge || 0,
            gender: clientPartner.intakeSurvey?.clientGenderAtBirth,
          }
        : undefined;

    dispatch(
      postIllustrationRequestRequest({
        insuranceQuoteId: quoteId,
        zipcode: client.intakeSurvey.clientZipCode,
        clientId: client.clientId,
        client: clientData,
        partner: partnerData,
        advisor: {
          advisorFirstName: advisor?.advisorFirstName,
          advisorLastName: advisor?.advisorLastName,
          organizationDisplayName: advisor?.organizationDisplayName,
          organizationName: advisor?.organizationName,
          advisorPhoneNumber: advisor?.advisorPhoneNumber,
        },
        premiumAmount: currentPremium,
        deathBenefit: isLifeInsuranceQuote(quote)
          ? Math.round(quote.deathBenefitInitial)
          : undefined,
        monthlyLTCBenefit: isLifeInsuranceQuote(quote)
          ? Math.round(quote.ltcBenefitMonthlyIndividual)
          : undefined,
        quoteType: isTraditionalLTC ? 'traditionalLTC' : 'insurance',
        fundingSourceId,
      }),
    );
  };

  // Securian's producer form requires a complete business address — any
  // missing field causes Securian's site to fall back to the shared login
  // account's saved profile on the final PDF. When the advisor's active
  // license is ADVISOR and their AdvisorAddresses row is incomplete, prompt
  // them first to either add their real address or switch to a WAG/custom
  // presenter before we dispatch the illustration.
  const isSecurianQuote =
    !isTraditionalLTC && quote.carrierName === CARRIER_NAMES.SECURIAN_FINANCIAL;
  const activeLicense = licensesData?.licenses?.find(
    license => license.licenseId === licensesData?.activeLicenseId,
  );
  const isActiveLicenseAdvisor = !activeLicense || activeLicense.ownerType === 'ADVISOR';
  const address = advisor?.advisorBusinessAddress;
  const hasCompleteBusinessAddress = Boolean(
    address?.streetAddress && address.city && address.state && address.zipCode,
  );
  const needsAdvisorAddressPrompt =
    isSecurianQuote && isActiveLicenseAdvisor && !hasCompleteBusinessAddress;

  const handleCreateIllustration = (
    event: React.MouseEvent,
    onShowQuoteGenerationInfoDialog: () => void,
  ) => {
    event.stopPropagation();

    if (isAnotherQuoteBeingCreated) {
      onShowQuoteGenerationInfoDialog();
      return;
    }

    if (needsAdvisorAddressPrompt) {
      setIsMissingAdvisorAddressDialogOpen(true);
      return;
    }

    startQuoteGeneration();
  };

  const closeMissingAdvisorAddressDialog = () => setIsMissingAdvisorAddressDialogOpen(false);

  const handleMissingAdvisorAddressResolved = () => {
    setIsMissingAdvisorAddressDialogOpen(false);
    startQuoteGeneration();
  };

  const hasIllustrationError = !!illustration.erroredRequests?.[quoteId];

  return {
    hasIllustrationPdf,
    hasIllustrationError,
    isCreatingIllustration,
    isAnotherQuoteBeingCreated,
    handleViewPdf,
    handleCreateIllustration,
    isMissingAdvisorAddressDialogOpen,
    closeMissingAdvisorAddressDialog,
    handleMissingAdvisorAddressResolved,
  };
}
